import { Suspense, lazy } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import "./App.css";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { CartProvider } from "./Context/Cart";
import { AuthProvider, useAuth } from "./Context/Auth";
import { LanguageProvider } from "./Context/Language";
import ScrollToTop from "./Components/ScrollToTop/ScrollToTop";
import CartDrawer from "./Components/Cart/Cart";
import CookieConsent from "./Components/CookieConsent/CookieConsent";
import Layout from "./Components/Layout/Layout";
import { setPostLoginRedirect } from "./Utils/storage";

const Home = lazy(() => import("./Pages/Home/Home"));
const Product = lazy(() => import("./Pages/Products/Product"));
const Deals = lazy(() => import("./Pages/Products/Deals"));
const Register = lazy(() => import("./Pages/Register/Register"));
const Login = lazy(() => import("./Pages/Login/Login"));
const VerifyEmail = lazy(() => import("./Pages/VerifyEmail/VerifyEmail"));
const ForgotPassword = lazy(
  () => import("./Pages/ForgotPassword/ForgotPassword"),
);
const ResetPassword = lazy(() => import("./Pages/ResetPassword/ResetPassword"));
const Products = lazy(() => import("./Pages/Products/Products"));
const Categories = lazy(() => import("./Pages/Categories/Categories"));
const Wishlist = lazy(() => import("./Pages/Wishlist/Wishlist"));
const Settings = lazy(() => import("./Pages/Settings/Settings"));
const Checkout = lazy(() => import("./Pages/Checkout/Checkout"));
const Orders = lazy(() => import("./Pages/Orders/Orders"));
const OrderDetails = lazy(() => import("./Pages/Orders/OrderDetails"));
const PlaceholderPage = lazy(() => import("./Pages/PlaceholderPage"));
const About = lazy(() => import("./Pages/About/About"));
const Partners = lazy(() => import("./Pages/Partners/Partners"));
const Careers = lazy(() => import("./Pages/Careers/Careers"));
const Press = lazy(() => import("./Pages/Press/Press"));
const Contact = lazy(() => import("./Pages/Contact/Contact"));
const Tickets = lazy(() => import("./Pages/Tickets/Tickets"));
const Ticket = lazy(() => import("./Pages/Tickets/Ticket"));
const FAQ = lazy(() => import("./Pages/Helpcenter/Faq"));
const PrivacyPolicy = lazy(() => import("./Pages/Policies/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./Pages/Policies/TermsOfService"));
const CookiePolicy = lazy(() => import("./Pages/Policies/CookiePolicy"));
const ShippingInfo = lazy(() => import("./Pages/Policies/ShippingInfo"));
const ReturnsRefunds = lazy(() => import("./Pages/Policies/ReturnsRefunds"));
const DiscountRedirect = lazy(
  () => import("./Pages/DiscountRedirect/DiscountRedirect"),
);
const NotFound = lazy(() => import("./Pages/NotFound/NotFound"));

const PageLoader = () => (
  <div
    style={{
      minHeight: "50vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "48px 0",
    }}
  >
    <div
      className="skeleton"
      style={{ width: 48, height: 48, borderRadius: 12 }}
    />
  </div>
);

function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) return null;

  if (!isAuthenticated) {
    const requestedPath = `${location.pathname}${location.search}${location.hash}`;
    setPostLoginRedirect(requestedPath);
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return children;
}

function PublicRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) return null;

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  return children;
}
const RedirectToContact = () => <Navigate to="/contact" replace />;

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
            <CartDrawer />
            <ToastContainer />
            <ScrollToTop />
            <CookieConsent />
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/products" element={<Products />} />
                  <Route path="/products/:slug" element={<Product />} />

                  <Route
                    path="/register"
                    element={
                      <PublicRoute>
                        <Register />
                      </PublicRoute>
                    }
                  />
                  <Route
                    path="/login"
                    element={
                      <PublicRoute>
                        <Login />
                      </PublicRoute>
                    }
                  />
                  <Route
                    path="/verify-email/:token"
                    element={<VerifyEmail />}
                  />
                  <Route path="/verify-email" element={<VerifyEmail />} />

                  <Route
                    path="/forgot-password"
                    element={
                      <PublicRoute>
                        <ForgotPassword />
                      </PublicRoute>
                    }
                  />
                  <Route
                    path="/reset-password/:token"
                    element={
                      <PublicRoute>
                        <ResetPassword />
                      </PublicRoute>
                    }
                  />

                  <Route path="/categories" element={<Categories />} />
                  <Route path="/deals" element={<Deals />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/careers" element={<Careers />} />
                  <Route
                    path="/blog"
                    element={<PlaceholderPage title="Blog" />}
                  />
                  <Route path="/press" element={<Press />} />
                  <Route path="/partners" element={<Partners />} />
                  <Route path="/help" element={<RedirectToContact />} />
                  <Route path="/shipping" element={<ShippingInfo />} />
                  <Route path="/returns" element={<ReturnsRefunds />} />
                  <Route path="/faq" element={<FAQ />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/terms" element={<TermsOfService />} />
                  <Route path="/cookies" element={<CookiePolicy />} />
                  <Route
                    path="/new"
                    element={<Navigate to="/products?sort=newest" replace />}
                  />
                  <Route
                    path="/best-sellers"
                    element={<Navigate to="/products?sort=popular" replace />}
                  />
                  <Route
                    path="/support"
                    element={<Navigate to="/contact" replace />}
                  />
                  <Route
                    path="/tickets"
                    element={
                      <ProtectedRoute>
                        <Tickets />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/tickets/:id"
                    element={
                      <ProtectedRoute>
                        <Ticket />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/checkout"
                    element={
                      <ProtectedRoute>
                        <Checkout />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/orders"
                    element={
                      <ProtectedRoute>
                        <Orders />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/orders/:id"
                    element={
                      <ProtectedRoute>
                        <OrderDetails />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/wishlist"
                    element={
                      <ProtectedRoute>
                        <Wishlist />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/settings"
                    element={
                      <ProtectedRoute>
                        <Settings />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/discount/:code"
                    element={<DiscountRedirect />}
                  />

                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </Layout>
          </BrowserRouter>
        </CartProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
